import { Feature, FeatureCollection, LineString, Polygon } from "geojson";

import { TwoDFlowData } from "@nens/deck-gl";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api, getWebsocketUrl } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { Asset, NodeProperties } from "../types/assets";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { OBJECT_TYPES, OTHER_PIPE_TYPE_KEY, PIPE_TYPES } from "./layerOneDMenu";
import { sendMessage } from "./mainGui";
import { Websocket, socketConnect } from "./websockets";

// Holds all metadata about the ongoing simulation

// https://github.com/nens/threedi-live-site/issues/820
export const ENDLESS_SIMULATION_DURATION_LIMIT = 3153600000; // seconds in 100 years, 100 * 365.25? * 24 * 60 * 60
export const PAUSE_TIMEOUT_SECONDS = 1800; // Ticket FRNT-987

export interface ObjectsPerType {
  [objectType: string]: string[];
}

export interface PipesPerType {
  [pipeType: string]: string[];
}

export type GridCellProperties = NodeProperties;
export type GridCell = Feature<Polygon, GridCellProperties>;
export type GridCellCollection = FeatureCollection<Polygon, GridCellProperties>;

export type GridFlowLineProperties = Record<string, unknown>;
export type GridFlowLine = Feature<LineString, GridCellProperties>;
export type GridCellFlowLineCollection = FeatureCollection<LineString, GridFlowLineProperties>;

interface SimulationState {
  startupReady: boolean;
  startRetries: number;
  selectedOrganisation: api.Organisation | null;
  threediModel: api.ThreediModel | null;
  simulation: api.Simulation | null;

  status: string;
  progress: number;
  time: number;
  load: number;
  rateLimiterOn: boolean;
  rateLimiterValue: number;

  geojson: FeatureCollection | null;
  gridcells: GridCellCollection;
  gridFlowLines: GridCellFlowLineCollection; //FeatureCollection;

  objects: { [id: string]: Asset };
  originalObjects: { [id: string]: Asset };
  objectsPerType: ObjectsPerType;
  pipesPerType: PipesPerType;

  lineData: {
    lineIndexOffset: number;
    u1: Float32Array; // water speed in m/s, sign says in which direction
    qw: Float32Array; // discharge in m³/s, sign *should be ignored!*, use Math.abs
  } | null;

  twoDFlowData: TwoDFlowData | null;

  simulationStateChangeRequested: boolean;
  simulationStateChangeFailed: boolean;

  downloadSimulationResults: boolean;
}

const INITIAL_STATE: SimulationState = {
  startupReady: false,
  startRetries: 0,
  selectedOrganisation: null,
  threediModel: null,
  simulation: null,
  status: "new",
  progress: 0,
  time: 0,
  load: 1.0,
  rateLimiterOn: false,
  rateLimiterValue: 30, // 30 simulation seconds equal 1 second in real time
  geojson: null,
  gridcells: { type: "FeatureCollection", features: [] },
  gridFlowLines: { type: "FeatureCollection", features: [] },
  objects: {},
  originalObjects: {},
  objectsPerType: {},
  pipesPerType: {},
  lineData: null,
  twoDFlowData: null,
  simulationStateChangeRequested: false,
  simulationStateChangeFailed: false,
  downloadSimulationResults: false,
};

// Action types

// Related to startup (see ./appState): startup must always end with
// a STARTUP_FINISHED action.

const STARTUP_FINISHED = "simulation/STARTUP_FINISHED";

interface StartupFinishedAction {
  type: typeof STARTUP_FINISHED;
  success: boolean; // True: set startupReady to true; false: revert this whole state!
}

const INCREASE_START_RETRIES = "simulation/INCREASE_START_RETRIES";

interface IncreaseStartRetries {
  type: typeof INCREASE_START_RETRIES;
}
const RESET_START_RETRIES = "simulation/RESET_START_RETRIES";

interface ResetStartRetries {
  type: typeof RESET_START_RETRIES;
}

// Normal actions

const SET_ORGANISATION_AND_THREEDIMODEL = "simulation/SET_ORGANISATION_AND_THREEDIMODEL";

interface SelectOrganisationAndThreediModelAction {
  type: typeof SET_ORGANISATION_AND_THREEDIMODEL;
  organisation: api.Organisation;
  threediModel: api.ThreediModel;
}

const SET_RATE_LIMITER_ON_OFF = "simulation/SET_RATE_LIMITER_ON_OFF";

interface SetRateLimiterOnOff {
  type: typeof SET_RATE_LIMITER_ON_OFF;
  turnOn: boolean;
}

const SET_SIMULATION = "simulation/SET_SIMULATION";

interface SetSimulationAction {
  type: typeof SET_SIMULATION;
  simulation: api.Simulation;
}

const RECEIVE_GEOJSON = "simulation/RECEIVE_GEOJSON";

interface ReceiveGeojsonAction {
  type: typeof RECEIVE_GEOJSON;
  geojson: FeatureCollection;
}

const RECEIVE_GRIDCELLS = "simulation/RECEIVE_GRIDCELLS";
interface ReceiveGridCellsAction {
  type: typeof RECEIVE_GRIDCELLS;
  gridcells: GridCellCollection;
}

const RECEIVE_GRIDFLOWLINES = "simulation/RECEIVE_GRIDFLOWLINES";
interface ReceiveGridFlowLinesAction {
  type: typeof RECEIVE_GRIDFLOWLINES;
  gridFlowLines: GridCellFlowLineCollection;
}

const RECEIVE_ONE_DEE_DATA = "simulation/RECEIVE_ONE_DEE_DATA";

interface ReceiveOneDeeDataAction {
  type: typeof RECEIVE_ONE_DEE_DATA;
  lineIndexOffset: number;
  u1: Float32Array;
  qw: Float32Array;
}

const RECEIVE_TWO_DEE_DATA = "simulation/RECEIVE_TWO_DEE_DATA";

interface ReceiveTwoDeeDataAction {
  type: typeof RECEIVE_TWO_DEE_DATA;
  ucx: Float32Array;
  ucy: Float32Array;
}

const REQUEST_SIMULATION_STATE_CHANGE = "simulation/REQUEST_SIMULATION_STATE_CHANGE";

interface RequestSimulationStateChange {
  type: typeof REQUEST_SIMULATION_STATE_CHANGE;
}

const SIMULATION_STATE_CHANGE_FAILED = "simulation/SIMULATION_STATE_CHANGE_FAILED";

interface SimulationStateChangeFailed {
  type: typeof SIMULATION_STATE_CHANGE_FAILED;
}

export const RECEIVED_WEBSOCKET_MESSAGE = "RECEIVED_WEBSOCKET_MESSAGE";

export interface WebsocketMessageAction {
  type: typeof RECEIVED_WEBSOCKET_MESSAGE;
  payload: any; // I don't believe there is a productive way to avoid this one.
}

const REQUEST_DOWNLOAD_SIMULATION_RESULTS = "REQUEST_DOWNLOAD_SIMULATION_RESULTS";

interface RequestDownloadSimulationResults {
  type: typeof REQUEST_DOWNLOAD_SIMULATION_RESULTS;
}

type SimulationAction =
  | StartupFinishedAction
  | IncreaseStartRetries
  | ResetStartRetries
  | SelectOrganisationAndThreediModelAction
  | SetRateLimiterOnOff
  | SetSimulationAction
  | BackToStartScreen
  | ReceiveGeojsonAction
  | ReceiveGridCellsAction
  | ReceiveGridFlowLinesAction
  | ReceiveOneDeeDataAction
  | ReceiveTwoDeeDataAction
  | WebsocketMessageAction
  | RequestSimulationStateChange
  | SimulationStateChangeFailed
  | RequestDownloadSimulationResults;

// Reducer

export default function reducer(state = INITIAL_STATE, action: SimulationAction): SimulationState {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case STARTUP_FINISHED: {
      const success = action.success;
      // If startup was unsuccessful, we *reset this whole state* so we go back
      // to the start screen. Should only be dispatched from ./appState.
      // If it was successful, we previously set startupReady to true, but now we do nothing instead. Instead we only set startupReady to true if a status of initialized is received over main websocket
      if (success) {
        return {
          ...state,
          // startupReady: true
        };
      } else {
        return INITIAL_STATE;
      }
    }
    case INCREASE_START_RETRIES:
      return {
        ...state,
        startRetries: state.startRetries + 1,
      };
    case RESET_START_RETRIES:
      return {
        ...state,
        startRetries: 0,
      };
    case SET_RATE_LIMITER_ON_OFF:
      return {
        ...state,
        rateLimiterOn: action.turnOn,
      };
    case SET_ORGANISATION_AND_THREEDIMODEL: {
      const selectedOrganisation = action.organisation;
      const threediModel = action.threediModel;
      return {
        ...state,
        selectedOrganisation,
        threediModel,
      };
    }
    case SET_SIMULATION: {
      const simulation = action.simulation;
      return {
        ...state,
        simulation,
      };
    }
    case RECEIVE_GEOJSON: {
      const objectsPerType: { [modelType: string]: string[] } = {};
      const objects: { [id: string]: Asset } = {};
      const pipesPerType: { [pipeType: string]: string[] } = {};

      OBJECT_TYPES.forEach((objectType) => (objectsPerType[objectType] = []));
      Object.keys(PIPE_TYPES).forEach((pipeType) => (pipesPerType[pipeType] = []));

      action.geojson.features.forEach((feature: Feature) => {
        if (!feature.properties || !feature.properties.site_id) return;

        const site_id: string = feature.properties.site_id;
        const model_type: string = feature.properties.model_type;

        if (model_type === "pipes") {
          let sewerage_type: keyof typeof PIPE_TYPES = feature.properties.sewerage_type;

          // If sewerage type is not included in the supported PIPE_TYPES change the key to other
          if (!Object.keys(PIPE_TYPES).includes(sewerage_type + "")) {
            sewerage_type = OTHER_PIPE_TYPE_KEY;
            feature.properties.sewerage_type = OTHER_PIPE_TYPE_KEY;
          }

          pipesPerType[sewerage_type].push(site_id);
        }

        if (!objectsPerType[model_type]) {
          console.error(feature);
        } else {
          objectsPerType[model_type].push(site_id);
        }

        objects[site_id] = feature as Asset;
      });

      return {
        ...state,
        geojson: action.geojson,
        objects,
        originalObjects: objects,
        objectsPerType,
        pipesPerType,
      };
    }
    case RECEIVE_GRIDFLOWLINES: {
      const { gridFlowLines } = action;
      return {
        ...state,
        gridFlowLines,
      };
    }
    case RECEIVE_GRIDCELLS: {
      const { gridcells } = action;
      return {
        ...state,
        gridcells,
      };
    }
    case RECEIVE_ONE_DEE_DATA:
      return {
        ...state,
        lineData: {
          lineIndexOffset: action.lineIndexOffset,
          u1: action.u1,
          qw: action.qw,
        },
      };
    case RECEIVE_TWO_DEE_DATA:
      return {
        ...state,
        twoDFlowData: {
          ucx: action.ucx,
          ucy: action.ucy,
        },
      };
    case REQUEST_SIMULATION_STATE_CHANGE:
      return {
        ...state,
        simulationStateChangeRequested: true,
        simulationStateChangeFailed: false,
      };
    case SIMULATION_STATE_CHANGE_FAILED:
      return {
        ...state,
        simulationStateChangeRequested: false,
        simulationStateChangeFailed: true,
        startRetries: 0,
      };
    case REQUEST_DOWNLOAD_SIMULATION_RESULTS:
      return {
        ...state,
        downloadSimulationResults: !state.downloadSimulationResults,
      };
    case RECEIVED_WEBSOCKET_MESSAGE: {
      const message = action.payload;

      if (!message.type || !message.data) {
        console.error("Unknown Websocket message (1):", typeof message, message);
        return state;
      }

      const type = message.type;
      const data = message.data;

      // helper function to update status of simulation

      const newStatusString = (status: api.SimulationStatus) => {
        if (status.name === "initialized") {
          if (status.paused) {
            return status.time ? "paused" : "initialized";
          } else {
            return "running";
          }
        } else {
          return status.name;
        }
      };

      // Update simulation state in reducer

      switch (type) {
        case "progress":
          return {
            ...state,
            progress: data.progress,
          };
        case "time":
          return {
            ...state,
            time: data.time,
            load: typeof data.load === "number" ? data.load : state.load,
          };
        case "status": {
          const status: api.SimulationStatus = data.data;
          if (
            !state.startupReady &&
            status &&
            (status.name === api.SimulationStatusNameEnum.INITIALIZED ||
              status.name === api.SimulationStatusNameEnum.CRASHED)
          ) {
            return {
              ...state,
              startupReady: true,
              status: newStatusString(status),
              simulationStateChangeRequested: false,
              simulationStateChangeFailed: false,
              startRetries: 0,
            };
          } else if (status) {
            return {
              ...state,
              status: newStatusString(status),
              simulationStateChangeRequested: false,
              simulationStateChangeFailed: false,
              startRetries: 0,
            };
          } else {
            // Note that there are other status messages for event notifications,
            // these are handled in other reducers
            return state;
          }
        }
        case "websocket":
          if (data.model && data.model === "SimulationStatus") {
            const status: api.SimulationStatus = data.data;
            return {
              ...state,
              status: newStatusString(status),
              simulationStateChangeRequested: false,
              simulationStateChangeFailed: false,
              startRetries: 0,
            };
          } else {
            console.error('Websocket message type "websocket" but not status');
            return state;
          }
        case "initial_events":
        case "event":
          // See existingEvents.ts
          return state;
        case "heartbeat":
          // Nice, but we don't need this for anything on the client side
          return state;
        default:
          console.error("Unknown Websocket message (2):", message.type, message);
          return state;
      }
    }
    default:
      return state;
  }
}

// Action creators

export const startupFailed =
  (message: string | JSX.Element): Thunk =>
  (dispatch) => {
    dispatch({
      type: STARTUP_FINISHED,
      success: false,
    });

    dispatch(sendMessage(message, 10000, "error"));
  };

// Previously a startup succes was dispatched if in appState.tsx in function startWithSimulation after main websocket was opened.
// This would previosuly set the redux simulation startupReady to true. Now we no longer do this. We instead set startupReady when a initialized created status is received over main websocket
// export const startupSuccess = () => {
//   return {
//     type: STARTUP_FINISHED,
//     success: true
//   };
// };

export const setSelectedOrganisationAndThreediModel = (
  organisation: api.Organisation,
  threediModel: api.ThreediModel
) => {
  return {
    type: SET_ORGANISATION_AND_THREEDIMODEL,
    organisation,
    threediModel,
  };
};

export const setSimulation = (simulation: api.Simulation) => {
  return {
    type: SET_SIMULATION,
    simulation,
  };
};

export const setRateLimiterOnOff = (turnOn: boolean) => {
  return {
    type: SET_RATE_LIMITER_ON_OFF,
    turnOn,
  };
};

export const receiveGeojson = (geojson: FeatureCollection) => {
  return {
    type: RECEIVE_GEOJSON,
    geojson,
  };
};
export const receiveGridCells = (gridcells: GridCellCollection) => {
  return {
    type: RECEIVE_GRIDCELLS,
    gridcells,
  };
};
export const receiveGridFlowLines = (gridFlowLines: GridCellFlowLineCollection) => {
  return {
    type: RECEIVE_GRIDFLOWLINES,
    gridFlowLines,
  };
};

export const requestDownloadSimulationResults = () => {
  return {
    type: REQUEST_DOWNLOAD_SIMULATION_RESULTS,
  };
};

export const openWebsocket = (): Thunk => (dispatch, getState) => {
  const simulation = getState().simulation.simulation;

  if (!simulation || !simulation.id) {
    throw new Error("tried to open websocket without simulation");
  }

  dispatch(
    socketConnect(
      getWebsocketUrl(`simulations/${simulation.id}/?initial_events=true&visualization=true`),
      Websocket.Main
    )
  );
};

export const startSimulation = (): Thunk => (dispatch, getState) => {
  const state = getState();
  const simulation = getSimulation(state);
  const rateLimitOn = getRateLimiterOn(state);
  const rateLimitValue = getRateLimiterValue(state);
  if (simulation === null) {
    return;
  }
  const startSimulationId = simulation.id;
  if (startSimulationId === undefined) return;

  dispatch({
    type: REQUEST_SIMULATION_STATE_CHANGE,
  });

  // POST {'name': 'start'} to /simulations/<simulation id>/actions/
  const v3Api = getV3Api();
  v3Api
    .simulationsActionsCreate({
      simulationPk: startSimulationId,
      data: {
        name: api.ActionNameEnum.START,
        maxRate: rateLimitOn ? rateLimitValue : undefined,
      },
    })
    .catch((error) => {
      try {
        error
          .json()
          .then((parsed: any) => {
            console.error("starting failed with error", parsed);
            if (
              parsed.errors &&
              parsed.errors.state ===
                "resource timedstructurecontrols has state 'processing' - must be 'valid'."
            ) {
              // https://github.com/nens/threedi-live-site/issues/755
              if (state.simulation.startRetries > 10) {
                // we show a yellow warning message:
                dispatch(
                  sendMessage(
                    "error.starting_simulation_failed_timedstructurecontrols_has_state_processing",
                    5000,
                    "warning"
                  )
                );
                dispatch({ type: RESET_START_RETRIES });
              } else {
                // or we keep retrying.
                window.setTimeout(() => {
                  dispatch({
                    type: INCREASE_START_RETRIES,
                  });
                  dispatch(startSimulation());
                }, 1400);
              }
            } else {
              dispatch({
                type: SIMULATION_STATE_CHANGE_FAILED,
              });
              dispatch(sendMessage("error.starting_simulation_failed", 5000, "error"));
            }
          })
          .catch((_errorParsing: Error) => {
            console.error("starting failed with error", error.message);
            dispatch({
              type: SIMULATION_STATE_CHANGE_FAILED,
            });
            dispatch(sendMessage("error.starting_simulation_failed", 5000, "error"));
          });
      } catch (errorParsingJson) {
        console.error("starting failed with error", error);
        dispatch({
          type: SIMULATION_STATE_CHANGE_FAILED,
        });
        dispatch(sendMessage("error.starting_simulation_failed", 5000, "error"));
      }
    });
};

export const rePauseSimulation =
  (timeOutSeconds: number): Thunk =>
  (_, getState) => {
    const simulation = getSimulation(getState());
    if (simulation === null) {
      return;
    }
    const rePauseSimulationId = simulation.id;
    if (rePauseSimulationId === undefined) return;

    const v3Api = getV3Api();
    v3Api
      .simulationsActionsCreate({
        simulationPk: rePauseSimulationId,
        data: {
          name: api.ActionNameEnum.START,
          maxRate: 1,
        },
      })
      .then(() => {
        v3Api.simulationsActionsCreate({
          simulationPk: rePauseSimulationId,
          data: {
            name: api.ActionNameEnum.PAUSE,
            timeout: timeOutSeconds,
          },
        });
      });
  };

export const pauseSimulation =
  (timeOutSeconds?: number): Thunk =>
  async (dispatch, getState) => {
    const simulation = getSimulation(getState());
    if (simulation === null) {
      return;
    }
    const pauseSimulationId = simulation.id;
    if (pauseSimulationId === undefined) return;

    dispatch({
      type: REQUEST_SIMULATION_STATE_CHANGE,
    });

    // POST {'name': 'pause'} to /simulations/<simulation id>/actions/
    const v3Api = getV3Api();
    try {
      await v3Api.simulationsActionsCreate({
        simulationPk: pauseSimulationId,
        data: {
          name: api.ActionNameEnum.PAUSE,
          timeout: timeOutSeconds || PAUSE_TIMEOUT_SECONDS,
        },
      });
    } catch (error: any) {
      try {
        const parsed = await error.message.json();
        console.error("pausing failed with error", parsed);
        dispatch({
          type: SIMULATION_STATE_CHANGE_FAILED,
        });
        dispatch(sendMessage("error.pausing_simulation_failed", 5000, "error"));
      } catch (errorParsingJson) {
        console.error("pausing failed with error", error.message);
        dispatch({
          type: SIMULATION_STATE_CHANGE_FAILED,
        });
        dispatch(sendMessage("error.pausing_simulation_failed", 5000, "error"));
        return;
      }
    }
  };

export const shutdownSimulation = (): Thunk => (_, getState) => {
  const simulation = getSimulation(getState());
  if (simulation === null) {
    return;
  }
  const shutdownSimulationId = simulation.id;
  if (shutdownSimulationId === undefined) return;

  // POST {'name': 'pause'} to /simulations/<simulation id>/actions/
  const v3Api = getV3Api();
  v3Api.simulationsActionsCreate({
    simulationPk: shutdownSimulationId,
    data: { name: api.ActionNameEnum.SHUTDOWN },
  });

  // This comment is placed here to identify code to test the crashing of the simulation.
  // Simulation crashes sometimes by accident, but it also crashes when the simulation is quit (as designed by backend).
  // Therefor we can quit the simulation, but not close the mainscreen and websockets and it is possible to test what happens if the simulation crashes.
  // Do this by commenting out the production code and uncommenting DEV_CRASH_TEST
  // After this quit the simulation without storing results.
  // The app will now not quit, but simulation will crash.
  // After this you can quit the simulation to recover (therefore is the if statement, so that the call will still be executed if already crashed).
  // see: https://nelen-schuurmans.atlassian.net/browse/FRNT-786
  // DEV_CRASH_TEST uncomment to test crash
  // const simulationStatus = getSimulationStatus(getState())
  // if (simulationStatus.status === api.SimulationStatusNameEnum.Crashed) {
  //   dispatch(backToStartScreen());
  // }
  // PRODUCTION_CODE comment out to test crash
};

export const receiveOneDeeData = (lineIndexOffset: number, u1: Float32Array, qw: Float32Array) => {
  return {
    type: RECEIVE_ONE_DEE_DATA,
    lineIndexOffset,
    u1,
    qw,
  };
};

export const receiveTwoDeeData = (ucx: Float32Array, ucy: Float32Array) => {
  return {
    type: RECEIVE_TWO_DEE_DATA,
    ucx,
    ucy,
  };
};

export const validateExtent = (extendCoordinates: [[number, number], [number, number]]) => {
  const oneOne = extendCoordinates[0][0];
  const oneTwo = extendCoordinates[0][1];
  const twoOne = extendCoordinates[1][0];
  const twoTwo = extendCoordinates[1][1];
  return oneOne !== twoOne && oneTwo !== twoTwo;
};

// Selectors
export const getSimulationState = (state: AppState): SimulationState => state.simulation;
export const getThreediModel = (state: AppState) => state.simulation.threediModel;

export const combineExtents = (extentA: api.Extent, extentB: api.Extent): api.Extent => {
  return {
    ...extentA,
    coordinates: [
      [
        Math.min(extentA.coordinates[0][0], extentB.coordinates[0][0]),
        Math.min(extentA.coordinates[0][1], extentB.coordinates[0][1]),
      ],
      [
        Math.max(extentA.coordinates[1][0], extentB.coordinates[1][0]),
        Math.max(extentA.coordinates[1][1], extentB.coordinates[1][1]),
      ],
    ],
  };
};
export const getModelExtent = (state: AppState): api.Extent | null => {
  const threediModel = getThreediModel(state);
  if (!threediModel) {
    return null;
  }
  const oneDValid =
    threediModel.extentOneD &&
    validateExtent([
      [threediModel.extentOneD.coordinates[0][0], threediModel.extentOneD.coordinates[0][1]],
      [threediModel.extentOneD.coordinates[1][0], threediModel.extentOneD.coordinates[1][1]],
    ]);
  const twoDValid =
    threediModel.extentTwoD &&
    validateExtent([
      [threediModel.extentTwoD.coordinates[0][0], threediModel.extentTwoD.coordinates[0][1]],
      [threediModel.extentTwoD.coordinates[1][0], threediModel.extentTwoD.coordinates[1][1]],
    ]);

  if (threediModel.extentOneD && threediModel.extentTwoD && oneDValid && twoDValid) {
    return combineExtents(threediModel.extentTwoD, threediModel.extentOneD);
  } else if (threediModel.extentOneD && oneDValid) {
    return threediModel.extentOneD;
  } else if (threediModel.extentTwoD && twoDValid) {
    return threediModel.extentTwoD;
  } else {
    return null;
  }
};

export const getInitialViewstate = (state: AppState) => {
  const extent = getModelExtent(state);

  const coords = extent ? extent.coordinates : null;

  const bounds = coords
    ? {
        latitude: coords[0][1] + (coords[1][1] - coords[0][1]) / 2,
        longitude: coords[0][0] + (coords[1][0] - coords[0][0]) / 2,
        zoom: 13,
        pitch: 0,
        bearing: 0,
      }
    : null;

  return bounds;
};

export const getModelPolygon = (state: AppState) => {
  const threediModel = getThreediModel(state);

  const coords =
    threediModel &&
    threediModel.extentOneD &&
    validateExtent([
      [threediModel.extentOneD.coordinates[0][0], threediModel.extentOneD.coordinates[0][1]],
      [threediModel.extentOneD.coordinates[1][0], threediModel.extentOneD.coordinates[1][1]],
    ])
      ? threediModel.extentOneD.coordinates
      : // @ts-ignore: Swagger mistakenly says extentOneD is a string. https://github.com/nens/threedi-api/issues/666
      threediModel &&
        threediModel.extentTwoD &&
        validateExtent([
          [threediModel.extentTwoD.coordinates[0][0], threediModel.extentTwoD.coordinates[0][1]],
          [threediModel.extentTwoD.coordinates[1][0], threediModel.extentTwoD.coordinates[1][1]],
        ])
      ? threediModel.extentTwoD.coordinates
      : null;

  const bounds = coords
    ? `POLYGON((` +
      `+${coords[0][0]}+${coords[0][1]},` +
      `+${coords[1][0]}+${coords[0][1]},` +
      `+${coords[1][0]}+${coords[1][1]},` +
      `+${coords[0][0]}+${coords[1][1]},` +
      `+${coords[0][0]}+${coords[0][1]}))`
    : null;

  return bounds;
};

export const getTwoDExtendModel = (state: AppState) => {
  const threediModel = getThreediModel(state);
  return threediModel && threediModel.extentTwoD;
};

// only call when sure that state.simulation.threediModel !== null
export const getThreediModelNotNull = (state: AppState) => {
  const threediModelOrNull = getThreediModel(state);
  if (threediModelOrNull === null) {
    throw new Error(
      "getThreediModelNotNull function called when state.simulation.threediModel is null"
    );
  } else {
    return threediModelOrNull;
  }
};

export const getSimulation = (state: AppState) => state.simulation.simulation!;
export const getSelectedOrganisation = (state: AppState) => state.simulation.selectedOrganisation;

export const hasModelBeenChosen = (state: AppState) => state.simulation.threediModel !== null;
export const getGeoJson = (state: AppState) => state.simulation.geojson;
export const getGridcells = (state: AppState) => state.simulation.gridcells;
export const getGridFlowLines = (state: AppState) => state.simulation.gridFlowLines;
export const getObjectsPerType = (state: AppState) => state.simulation.objectsPerType;
export const getPipesPerType = (state: AppState) => state.simulation.pipesPerType;
export const getObjects = (state: AppState) => state.simulation.objects;

export const getSimulationStatus = (state: AppState) => {
  const s = state.simulation;
  return {
    status: s.status,
    time: s.time,
    progress: s.progress,
  };
};

export const getTime = (state: AppState) => state.simulation.time as number;
export const getLoad = (state: AppState) => state.simulation.load as number;
export const getRateLimiterOn = (state: AppState) => state.simulation.rateLimiterOn as boolean;
export const getRateLimiterValue = (state: AppState) => state.simulation.rateLimiterValue as number;

export const getOneDeeData = (state: AppState) => state.simulation.lineData;

export const getTwoDeeData = (state: AppState) => state.simulation.twoDFlowData;

export const getOriginalObjects = (state: AppState) => state.simulation.originalObjects;

export const getIsSimulationEndless = (state: AppState) => {
  if (
    state.simulation.simulation &&
    state.simulation.simulation.duration &&
    state.simulation.simulation.duration >= ENDLESS_SIMULATION_DURATION_LIMIT
  ) {
    return true;
  } else {
    return false;
  }
};

export const getSimulationDuration = (state: AppState) => {
  return (state.simulation.simulation && state.simulation.simulation.duration) || 0;
};

export const getSimulationStateChangeRequested = (state: AppState) => {
  return state.simulation.simulationStateChangeRequested;
};
export const getSimulationStateChangeFailed = (state: AppState) => {
  return state.simulation.simulationStateChangeFailed;
};

export const getDownloadSimulationResults = (state: AppState) => {
  return state.simulation.downloadSimulationResults;
};
